import { LitElement, html, css } from 'lit';

export class FlashCard extends LitElement {
  static styles = css`
    :host {
      display: block;
      perspective: 1000px;
      touch-action: pan-y;
    }

    .card-wrapper {
      width: 100%;
      position: relative;
    }

    .card-container {
      width: 100%;
      min-height: 280px;
      position: relative;
      transform-style: preserve-3d;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
    }

    /* Only flip transition - no other transitions */
    .card-container {
      transition: none;
    }

    .card-container.flipped {
      transform: rotateY(180deg);
      transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* During swipe - no transition */
    .card-container.swiping {
      transition: none;
    }

    /* Snap back */
    .card-container.swipe-back {
      transition: transform 0.3s ease-out;
    }

    .card-face {
      position: absolute;
      width: 100%;
      min-height: 280px;
      backface-visibility: hidden;
      border-radius: 20px;
      padding: 24px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    }

    .card-front {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .card-back {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: white;
      transform: rotateY(180deg);
    }

    .word {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 8px;
      text-align: center;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .pronunciation {
      font-size: 1rem;
      opacity: 0.9;
      margin-bottom: 12px;
      font-style: italic;
    }

    .meaning {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 16px;
      text-align: center;
      line-height: 1.4;
    }

    .difficulty {
      position: absolute;
      top: 16px;
      right: 16px;
      font-size: 0.75rem;
      padding: 4px 10px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.25);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .tap-hint {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 0.75rem;
      opacity: 0.7;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .tap-hint svg { width: 16px; height: 16px; }

    .swipe-indicator {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s ease;
      pointer-events: none;
      z-index: 10;
    }

    .swipe-indicator.left {
      left: 16px;
      background: rgba(255, 255, 255, 0.9);
      color: #667eea;
    }

    .swipe-indicator.right {
      right: 16px;
      background: rgba(255, 255, 255, 0.9);
      color: #667eea;
    }

    .swipe-indicator.show { opacity: 1; }
    .swipe-indicator svg { width: 24px; height: 24px; }

    @media (max-width: 480px) {
      .card-face { min-height: 260px; padding: 20px; }
      .word { font-size: 1.75rem; }
      .meaning { font-size: 1.1rem; }
    }
  `;

  static properties = {
    word: { type: Object },
    flipped: { type: Boolean }
  };

  constructor() {
    super();
    this.word = null;
    this.flipped = false;
    
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.currentX = 0;
    this.isSwiping = false;
    this.isHorizontalSwipe = false;
    this.SWIPE_THRESHOLD = 80;
  }

  _flipCard() {
    if (this.isSwiping) return;
    this.flipped = !this.flipped;
    this.dispatchEvent(new CustomEvent('card-flip', {
      detail: { flipped: this.flipped },
      bubbles: true,
      composed: true
    }));
  }

  _onTouchStart(e) {
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.currentX = 0;
    this.isSwiping = false;
    this.isHorizontalSwipe = false;
  }

  _onTouchMove(e) {
    const touch = e.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    
    if (!this.isSwiping) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      if (absY > absX && absY > 10) return;
      
      if (absX > 10) {
        this.isSwiping = true;
        this.isHorizontalSwipe = true;
      }
    }
    
    if (this.isHorizontalSwipe) {
      e.preventDefault();
      this.currentX = deltaX;
      
      const card = this.shadowRoot.querySelector('.card-container');
      if (card) {
        card.classList.add('swiping');
        card.style.transform = `translateX(${deltaX}px) ${this.flipped ? 'rotateY(180deg)' : ''}`;
      }
      
      this._updateSwipeIndicators(deltaX);
    }
  }

  _onTouchEnd(e) {
    if (!this.isHorizontalSwipe) return;
    
    const card = this.shadowRoot.querySelector('.card-container');
    
    if (Math.abs(this.currentX) > this.SWIPE_THRESHOLD) {
      const direction = this.currentX > 0 ? 'right' : 'left';
      
      if (card) {
        const offScreenX = direction === 'right' ? window.innerWidth : -window.innerWidth;
        card.style.transition = 'transform 0.2s ease-out';
        card.style.transform = `translateX(${offScreenX}px) ${this.flipped ? 'rotateY(180deg)' : ''}`;
      }
      
      setTimeout(() => {
        this.dispatchEvent(new CustomEvent(`swipe-${direction}`, {
          bubbles: true,
          composed: true
        }));
        this._resetCardPosition();
      }, 200);
    } else {
      this._resetCardPosition();
    }
    
    this._hideSwipeIndicators();
    this.isSwiping = false;
    this.isHorizontalSwipe = false;
    this.currentX = 0;
  }

  _resetCardPosition() {
    const card = this.shadowRoot.querySelector('.card-container');
    if (card) {
      card.classList.remove('swiping');
      card.classList.add('swipe-back');
      card.style.transform = this.flipped ? 'rotateY(180deg)' : '';
      
      setTimeout(() => {
        card.classList.remove('swipe-back');
        card.style.transform = '';
      }, 300);
    }
  }

  _updateSwipeIndicators(deltaX) {
    const leftIndicator = this.shadowRoot.querySelector('.swipe-indicator.left');
    const rightIndicator = this.shadowRoot.querySelector('.swipe-indicator.right');
    
    if (deltaX > this.SWIPE_THRESHOLD * 0.5) {
      rightIndicator?.classList.add('show');
      leftIndicator?.classList.remove('show');
    } else if (deltaX < -this.SWIPE_THRESHOLD * 0.5) {
      leftIndicator?.classList.add('show');
      rightIndicator?.classList.remove('show');
    } else {
      leftIndicator?.classList.remove('show');
      rightIndicator?.classList.remove('show');
    }
  }

  _hideSwipeIndicators() {
    const leftIndicator = this.shadowRoot.querySelector('.swipe-indicator.left');
    const rightIndicator = this.shadowRoot.querySelector('.swipe-indicator.right');
    leftIndicator?.classList.remove('show');
    rightIndicator?.classList.remove('show');
  }

  render() {
    if (!this.word) return html``;

    return html`
      <div class="card-wrapper"
        @touchstart="${this._onTouchStart}"
        @touchmove="${this._onTouchMove}"
        @touchend="${this._onTouchEnd}"
      >
        <div class="swipe-indicator left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </div>
        <div class="swipe-indicator right">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>

        <div 
          class="card-container ${this.flipped ? 'flipped' : ''}" 
          @click="${this._flipCard}"
          role="button"
          tabindex="0"
          @keydown="${(e) => e.key === 'Enter' && this._flipCard()}"
          aria-label="Flashcard. Druk om te draaien."
        >
          <div class="card-face card-front">
            <span class="difficulty">${this.word.difficulty}</span>
            <div class="word">${this.word.word}</div>
            <div class="pronunciation">${this.word.pronunciation}</div>
            <div class="tap-hint">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M7 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
                <path d="M11 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
                <path d="M15 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
              </svg>
              Tap om te draaien
            </div>
          </div>
          <div class="card-face card-back">
            <span class="difficulty">${this.word.difficulty}</span>
            <div class="meaning">${this.word.meaning}</div>
            <div class="tap-hint">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M7 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
                <path d="M11 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
                <path d="M15 11v 4a2 2 0 0 0 4 0v-5a2 2 0 0 0-4 0v1"></path>
              </svg>
              Tap om te draaien back
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('flash-card', FlashCard);
